import {
  Autocomplete,
  MenuItem,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from "@mui/material"
import { FormErrorPrompt } from "@tc/ui-dfe/components/Form/FormErrorPrompt"
import {
  FormWarningPrompt,
  warningBorderSx,
} from "@tc/ui-dfe/components/Form/FormWarningPrompt"
import { useFieldWarning } from "@tc/ui-dfe/hooks/config/useFieldWarnings"
import { DropdownIcon } from "@tc/ui-dfe/icons"
import { LabelValue } from "@tc/ui-dfe/utils/types"
import { ReactNode, SyntheticEvent, useId, useState } from "react"
import { get, useController, useFormContext } from "react-hook-form"

export type FormTextfieldProps = TextFieldProps & {
  errorMessage?: string
  name: string
  options: readonly LabelValue[]
  inline?: boolean
  multiple?: boolean
  searchable?: boolean
}

const renderHelperText = (
  hasError: boolean,
  hasWarning: boolean,
  errorMessage?: string,
  warningMessage?: string,
): ReactNode | undefined => {
  if (hasError) return <FormErrorPrompt message={errorMessage} />
  if (hasWarning) return <FormWarningPrompt message={warningMessage} />
  return undefined
}

const buildTextFieldSx = (
  inline: boolean,
  hasError: boolean,
  fieldWarning: string | undefined,
): SxProps<Theme> => ({
  ...(inline && {
    "& .MuiInputBase-root": {
      "& .MuiInputBase-input": { px: 1, py: 0.2 },
    },
  }),
  flexGrow: 1,
  "& .Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: "forest.main",
  },
  "& .Mui-error .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
  ...warningBorderSx(hasError, fieldWarning),
})

export const FormSelector = (props: FormTextfieldProps) => {
  const {
    id,
    placeholder,
    name,
    errorMessage,
    disabled,
    defaultValue: _defaultValue,
    options = [],
    inline = false,
    multiple = false,
    searchable = false,
    ...textFieldProps
  } = props
  const autoId = useId()
  const _id = id || autoId
  const { control } = useFormContext()
  const {
    field,
    formState: { errors },
  } = useController({ name, control })
  const error = get(errors, name)
  const fieldWarning = useFieldWarning(name)
  const hasError = !!error?.message || !!errorMessage
  const hasWarning = !hasError && !!fieldWarning
  const [inputValue, setInputValue] = useState("")

  const helperText = renderHelperText(
    hasError,
    hasWarning,
    (error?.message || errorMessage) as string,
    fieldWarning,
  )
  const sx = buildTextFieldSx(inline, hasError, fieldWarning)

  if (searchable) {
    const selectedOption =
      options.find((opt) => `${opt.value}` === `${field.value}`) ?? null
    return (
      <Autocomplete
        id={_id}
        options={options as LabelValue[]}
        value={selectedOption}
        sx={{ width: "100%" }}
        onBlur={field.onBlur}
        onChange={(
          _e: SyntheticEvent<Element, Event>,
          selected: LabelValue | null,
        ) => {
          field.onChange(selected?.value ?? "")
        }}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue)
        }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        isOptionEqualToValue={(option, selected) =>
          `${option.value}` === `${selected.value}`
        }
        renderOption={(optionProps, option, { selected: isSelected }) => (
          <MenuItem {...optionProps} key={optionProps.id} selected={isSelected}>
            {option.label}
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder ?? "Select"}
            error={hasError}
            helperText={helperText}
            sx={sx}
          />
        )}
        disabled={disabled}
        autoHighlight
      />
    )
  }

  const handleRenderValue = (value: unknown): React.ReactNode => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return (
        <Typography
          variant="body1"
          data-testid="test-formSelect-placeholder"
          sx={{
            color: (theme) => theme.palette.text.disabled,
            whiteSpace: "normal",
          }}
        >
          {placeholder ?? "Select"}
        </Typography>
      )
    }

    if (multiple && Array.isArray(value)) {
      const labels = value
        .map(
          (v) => options.find((option) => `${option.value}` === `${v}`)?.label,
        )
        .filter(Boolean)
        .join(", ")
      return <Typography>{labels}</Typography>
    }

    const description = options.find(
      (option) => `${option.value}` === `${value}`,
    )?.label
    return <Typography>{description}</Typography>
  }

  return (
    <TextField
      {...field}
      {...textFieldProps}
      value={field.value ?? (multiple ? [] : "")}
      sx={{
        ...sx,
        "& .MuiSelect-select": { whiteSpace: "normal !important" },
      }}
      id={_id}
      error={hasError}
      helperText={helperText}
      disabled={disabled}
      select
      slotProps={{
        select: {
          IconComponent: DropdownIcon,
          displayEmpty: true,
          renderValue: (value: unknown) => handleRenderValue(value),
          multiple,
        },
      }}
    >
      <MenuItem value={""} disabled>
        Select
      </MenuItem>
      {options?.map((option, i) => (
        <MenuItem key={`${i}-${option.value}`} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  )
}
